import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { TrendingUp, Upload, ListChecks, Settings2, AlertTriangle, PlusCircle, Trash2, PiggyBank, Sparkles, Loader2 } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/kaizen";

const SEV = { high: "text-[#D90429]", medium: "text-amber-400", info: "text-white/60" };
const TABS = [
  { id: "portfolio", label: "Portefeuille", icon: TrendingUp },
  { id: "import", label: "Import CSV", icon: Upload },
  { id: "transactions", label: "Transactions", icon: ListChecks },
  { id: "ai", label: "Résumé IA", icon: Sparkles },
  { id: "settings", label: "Réglages", icon: Settings2 },
];

const money = (n) => (n ?? 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD" });

export default function InvestmentModule() {
  const [tab, setTab] = useState("portfolio");
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [settings, setSettings] = useState(null);

  const loadSummary = async () => {
    try { const { data } = await api.get("/investment/summary"); setSummary(data); }
    catch { toast.error("Chargement du portefeuille impossible"); }
  };
  const loadTxs = async () => {
    try { const { data } = await api.get("/investment/transactions"); setTxs(data.transactions || []); } catch {}
  };
  const loadSettings = async () => {
    try { const { data } = await api.get("/investment/settings"); setSettings(data); } catch {}
  };
  useEffect(() => { loadSummary(); loadTxs(); loadSettings(); }, []);

  return (
    <div className="max-w-5xl mx-auto" data-testid="investment-module">
      <PageHeader icon={PiggyBank} title="Investissement"
        subtitle="Votre chef d'orchestre de discipline : suivi du portefeuille, allocation, concentration et alertes. Aucun conseil d'achat/vente — que des faits pour décider vous-même." />

      <div className="flex gap-2 mb-6 border-b border-white/10" data-testid="investment-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`inv-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${tab === t.id ? "border-[#D90429] text-white" : "border-transparent text-white/50 hover:text-white/80"}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "portfolio" && <Portfolio summary={summary} reload={loadSummary} />}
      {tab === "import" && <ImportCsv onDone={() => { loadSummary(); loadTxs(); }} />}
      {tab === "transactions" && <Transactions txs={txs} reload={() => { loadTxs(); loadSummary(); }} />}
      {tab === "ai" && <AiSummary />}
      {tab === "settings" && <SettingsTab settings={settings} reload={loadSettings} />}
    </div>
  );
}

function AiSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/investment/ai-summary"); setData(data.summary); }
    catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async (force) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/investment/ai-summary/generate${force ? "?force=true" : ""}`);
      setData(data.summary);
      toast.success(data.cached ? "Résumé de la semaine déjà généré" : "Résumé IA généré");
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération impossible"); }
    finally { setBusy(false); }
  };

  return (
    <SectionCard title="Résumé IA hebdomadaire">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-white/60 text-sm">
          Synthèse factuelle (performance, dividendes, concentration, anomalies). <strong>Aucun conseil d'achat/vente.</strong>
        </p>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => generate(false)} disabled={busy} data-testid="ai-generate-btn"
            className="bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-50 text-white px-4 py-2 text-sm flex items-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Générer
          </button>
          {data && (
            <button onClick={() => generate(true)} disabled={busy} data-testid="ai-regenerate-btn"
              className="border border-white/20 hover:border-white/40 text-white/80 px-3 py-2 text-sm">
              Regénérer
            </button>
          )}
        </div>
      </div>
      {loading ? (
        <p className="text-white/40 text-sm">Chargement…</p>
      ) : !data ? (
        <p className="text-white/40 text-sm" data-testid="ai-empty">Aucun résumé encore. Clique « Générer » pour créer le résumé de la semaine.</p>
      ) : (
        <div data-testid="ai-summary-content">
          <div className="text-[11px] uppercase tracking-wide text-white/40 mb-2">
            Semaine {data.week} · généré le {String(data.generated_at).slice(0, 10)}
          </div>
          <div className="prose prose-invert prose-sm max-w-none text-white/85 whitespace-pre-wrap leading-relaxed text-sm">
            {data.text}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function AllocBar({ items }) {
  return (
    <div className="space-y-2">
      {items.map((a) => (
        <div key={a.label}>
          <div className="flex justify-between text-xs text-white/60 mb-1"><span>{a.label}</span><span>{a.pct}% · {money(a.value)}</span></div>
          <div className="h-2 bg-white/10"><div className="h-2 bg-[#D90429]" style={{ width: `${Math.min(a.pct, 100)}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function Portfolio({ summary, reload }) {
  const [form, setForm] = useState({ account_type: "TFSA", symbol: "", name: "", quantity: "", market_value: "", category: "non classé" });
  if (!summary) return <p className="text-white/50 text-sm">Chargement…</p>;

  const addHolding = async () => {
    if (!form.symbol) return toast.error("Symbole requis");
    try {
      await api.post("/investment/holdings", { ...form, quantity: Number(form.quantity) || 0, market_value: Number(form.market_value) || 0 });
      toast.success("Avoir ajouté");
      setForm({ ...form, symbol: "", name: "", quantity: "", market_value: "" });
      reload();
    } catch { toast.error("Ajout impossible"); }
  };
  const delHolding = async (id) => { await api.delete(`/investment/holdings/${id}`); reload(); };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="investment-totaux">
        {[
          { l: "Valeur totale", v: money(summary.total_value) },
          { l: "Dépôts (année)", v: money(summary.net_deposits_ytd) },
          { l: "Dividendes suivis", v: money(summary.dividends_total) },
          { l: "Position dominante", v: `${summary.top_holding_pct}%`, accent: summary.top_holding_pct >= 70 },
        ].map((s) => (
          <div key={s.l} className={`border p-4 ${s.accent ? "border-[#D90429]/50 bg-[#D90429]/10" : "border-white/10 bg-black/30"}`}>
            <p className="text-xs uppercase tracking-wider text-white/50">{s.l}</p>
            <p className={`text-2xl font-semibold mt-1 ${s.accent ? "text-[#D90429]" : "text-white"}`}>{s.v}</p>
          </div>
        ))}
      </div>

      {summary.alerts.length > 0 && (
        <SectionCard title="Alertes de discipline">
          <div className="space-y-2" data-testid="investment-alertes">
            {summary.alerts.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <AlertTriangle size={15} className={`${SEV[a.severity] || "text-white/60"} shrink-0 mt-0.5`} />
                <span className="text-white/80">{a.message}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <SectionCard title="Allocation par compte">
          {summary.allocation_by_account.length ? <AllocBar items={summary.allocation_by_account} /> : <p className="text-white/40 text-sm">Aucune donnée.</p>}
        </SectionCard>
        <SectionCard title="Allocation par thème">
          {summary.allocation_by_category.length ? <AllocBar items={summary.allocation_by_category} /> : <p className="text-white/40 text-sm">Aucune donnée.</p>}
        </SectionCard>
      </div>

      <SectionCard title={`Avoirs (${summary.holdings.length})`}>
        <div className="grid sm:grid-cols-6 gap-2 mb-4 text-sm" data-testid="add-holding-form">
          <input placeholder="Symbole" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} data-testid="holding-symbol" className="bg-black/40 border border-white/15 px-2 py-2 uppercase" />
          <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} className="bg-black/40 border border-white/15 px-2 py-2">
            {["TFSA", "RRSP", "non-enregistré", "crypto", "USD"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-black/40 border border-white/15 px-2 py-2">
            {["non classé", "ETF large", "IA", "dividende", "spéculatif", "crypto", "revenu"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <input placeholder="Quantité" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="bg-black/40 border border-white/15 px-2 py-2" />
          <input placeholder="Valeur $" value={form.market_value} onChange={(e) => setForm({ ...form, market_value: e.target.value })} data-testid="holding-value" className="bg-black/40 border border-white/15 px-2 py-2" />
          <button onClick={addHolding} data-testid="add-holding-btn" className="bg-[#D90429] hover:bg-[#b8031f] text-white flex items-center justify-center gap-1"><PlusCircle size={16} /> Ajouter</button>
        </div>
        {summary.holdings.length === 0 ? <p className="text-white/40 text-sm">Aucun avoir. Ajoutez-en ou importez un CSV.</p> : (
          <div className="space-y-1.5" data-testid="holdings-list">
            {summary.holdings.map((h, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-1.5">
                <span className="text-white/80"><b>{h.symbol}</b> <span className="text-white/40">· {h.account_type} · {h.category}</span></span>
                <span className="text-white/60 text-xs">{money(h.market_value)} · {h.pct}%</span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function ImportCsv({ onDone }) {
  const [csvText, setCsvText] = useState("");
  const [kind, setKind] = useState("transactions");
  const [account, setAccount] = useState("TFSA");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setCsvText(r.result);
    r.readAsText(f);
  };

  const runPreview = async () => {
    if (!csvText.trim()) return toast.error("Choisissez un fichier CSV");
    setBusy(true);
    try {
      const { data } = await api.post("/investment/import/preview", { csv_text: csvText, kind, default_account_type: account });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Prévisualisation impossible"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/investment/import/commit", { csv_text: csvText, kind, default_account_type: account });
      toast.success(`✅ Import : ${data.inserted} ajoutée(s) · ${data.duplicates_skipped} doublon(s) ignoré(s) · ${data.needs_review} à revoir`);
      setPreview(null); setCsvText(""); onDone();
    } catch { toast.error("Import impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="import-csv-tab">
      <SectionCard title="Importer un CSV Wealthsimple">
        <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="import-kind" className="bg-black/40 border border-white/15 px-3 py-2">
            <option value="transactions">Transactions / Activité</option>
            <option value="holdings">Avoirs (Holdings)</option>
          </select>
          <select value={account} onChange={(e) => setAccount(e.target.value)} className="bg-black/40 border border-white/15 px-3 py-2">
            {["TFSA", "RRSP", "non-enregistré", "crypto", "USD"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="import-file" className="text-white/70 text-sm" />
          <button onClick={runPreview} disabled={busy} data-testid="import-preview-btn" className="bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-50 text-white px-4 py-2">Prévisualiser (dry-run)</button>
        </div>
        <p className="text-xs text-white/40">Détection souple des colonnes · anti-doublon · les lignes ambiguës vont en « à revoir ». Rien n'est écrit avant validation.</p>
      </SectionCard>

      {preview && (
        <SectionCard title="Résultat de la simulation">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="import-preview-totaux">
            {[
              { l: "À importer", v: preview.totals.to_import, c: "text-emerald-400" },
              { l: "Doublons", v: preview.totals.duplicates, c: "text-white/60" },
              { l: "À revoir", v: preview.totals.needs_review, c: "text-amber-400" },
              { l: "Total lignes", v: preview.totals.total_rows, c: "text-white" },
            ].map((s) => (
              <div key={s.l} className="border border-white/10 bg-black/30 p-3">
                <p className="text-xs uppercase tracking-wider text-white/50">{s.l}</p>
                <p className={`text-2xl font-semibold ${s.c}`}>{s.v}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-white/50 mb-3">Colonnes détectées : {Object.entries(preview.mapping).map(([k, v]) => `${k}→${v}`).join(", ") || "aucune"}</p>
          {preview.sample_to_import.length > 0 && (
            <div className="mb-3">
              <p className="text-sm text-white/70 mb-1">Aperçu à importer :</p>
              <div className="space-y-1 text-xs text-white/60">
                {preview.sample_to_import.slice(0, 8).map((r, i) => (
                  <div key={i} className="border-b border-white/5 pb-1">{r.date} · {r.symbol || "—"} · {r.type} · {money(r.amount)} · {r.account_type}</div>
                ))}
              </div>
            </div>
          )}
          <button onClick={commit} disabled={busy || preview.totals.to_import === 0} data-testid="import-commit-btn" className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white px-5 py-2.5">
            Confirmer l'import de {preview.totals.to_import} ligne(s)
          </button>
        </SectionCard>
      )}
    </div>
  );
}

function Transactions({ txs, reload }) {
  const del = async (id) => { await api.delete(`/investment/transactions/${id}`); reload(); };
  return (
    <SectionCard title={`Transactions (${txs.length})`}>
      {txs.length === 0 ? <p className="text-white/40 text-sm">Aucune transaction. Importez un CSV.</p> : (
        <div className="space-y-1.5" data-testid="transactions-list">
          {txs.slice(0, 100).map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-1.5">
              <span className="text-white/80">{t.date} · <b>{t.symbol || "—"}</b> · {t.type} <span className="text-white/40">({t.account_type})</span></span>
              <span className="flex items-center gap-3">
                <span className="text-white/60 text-xs">{money(t.amount)}</span>
                <button onClick={() => del(t.id)} className="text-white/30 hover:text-[#D90429]"><Trash2 size={14} /></button>
              </span>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function SettingsTab({ settings, reload }) {
  const [f, setF] = useState(settings || { tfsa_room_total: 0, concentration_threshold: 70, contribution_reminder_days: 30 });
  useEffect(() => { if (settings) setF(settings); }, [settings]);
  const save = async () => {
    try {
      await api.put("/investment/settings", {
        tfsa_room_total: Number(f.tfsa_room_total) || 0,
        concentration_threshold: Number(f.concentration_threshold) || 70,
        contribution_reminder_days: Number(f.contribution_reminder_days) || 30,
      });
      toast.success("Réglages enregistrés"); reload();
    } catch { toast.error("Enregistrement impossible"); }
  };
  return (
    <SectionCard title="Réglages des alertes">
      <div className="space-y-4 max-w-md" data-testid="investment-settings">
        {[
          { k: "tfsa_room_total", l: "Espace TFSA total disponible ($)", h: "Pour l'alerte d'espace TFSA restant." },
          { k: "concentration_threshold", l: "Seuil de concentration (%)", h: "Alerte si une position dépasse ce %." },
          { k: "contribution_reminder_days", l: "Rappel de contribution (jours)", h: "Alerte si aucun dépôt depuis N jours." },
        ].map((s) => (
          <div key={s.k}>
            <label className="block text-sm text-white/70 mb-1">{s.l}</label>
            <input type="number" value={f[s.k]} onChange={(e) => setF({ ...f, [s.k]: e.target.value })} data-testid={`setting-${s.k}`} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-white" />
            <p className="text-xs text-white/40 mt-1">{s.h}</p>
          </div>
        ))}
        <button onClick={save} data-testid="save-settings-btn" className="bg-[#D90429] hover:bg-[#b8031f] text-white px-5 py-2.5">Enregistrer</button>
      </div>
    </SectionCard>
  );
}
